/**
 * The services piece: every service the studio lists, each opening its page.
 * With a limit that leaves some out, See all N services opens the services
 * page (Gabriel 2026-10-20, layout 1 Tighter). Draws nothing when the studio
 * lists none.
 */
import type { SiteServiceSummaryDto } from '@telify/shared';

import { definePiece } from '../../contract/define';
import type { MineMark } from '../events-row/contract';

export const contract = definePiece({
  name: 'services',
  description: 'Every service the studio lists, in its order, each with its price and a Book link to its page.',
  placeable: true,
  options: {
    layout: { type: 'enum', values: ['list', 'grid'], default: 'list', description: 'Rows with the details in columns, or cards in a grid.' },
    limit: { type: 'number', min: 0, max: 100, default: 0, description: 'How many services to show at most; 0 shows every one.' },
    showPhoto: { type: 'boolean', default: true, description: "Show each service's main photo.", showsPart: 'photo' },
    showSummary: { type: 'boolean', default: true, description: "Show each service's short line.", showsPart: 'summary' },
    showPrice: { type: 'boolean', default: true, description: "Show each service's price.", showsPart: 'price' },
    heading: { type: 'string', default: 'Services', maxLength: 80, description: 'The section heading; null for none.' },
    eyebrow: { type: 'string', default: 'What we offer', maxLength: 60, description: 'The small label above the heading; null for none.' },
    intro: {
      type: 'string',
      default: 'Choose a service to see its open times. Pick a time, then sign in with Telegram to confirm.',
      maxLength: 240,
      description: 'The line beside the heading; null for none.',
    },
    showTimes: {
      type: 'boolean',
      default: false,
      description: "Show each service's next open times over four days, one service under another; a time opens the service's page with it picked.",
      showsPart: 'times',
    },
    mine: {
      type: 'boolean',
      default: false,
      description: "For a signed-in client, mark each service they have booked with their next booking of it, where it stands and a button opening it. A guest sees no mark.",
      showsPart: 'mine',
    },
  },
  parts: {
    section: { description: 'The whole section.', required: false },
    eyebrow: { description: 'The small label above the heading.', required: false },
    heading: { description: 'The section heading.', required: false, role: 'title' },
    intro: { description: 'The line beside the heading.', required: false },
    list: { description: 'The list of services.', required: true },
    item: { description: 'One service; the whole item opens its page.', required: true },
    photo: { description: "The service's main photo.", required: false, role: 'photo' },
    name: { description: "The service's name.", required: true, role: 'title' },
    summary: { description: "The service's short line.", required: false },
    duration: { description: 'How long a session lasts.', required: false },
    format: { description: 'In person, online or by phone.', required: false },
    price: { description: 'The price, Free, or from the lowest price.', required: true },
    book: { description: "The Book link to the service's page.", required: true },
    'see-all': { description: 'The See all services link to the services page; only when the limit option leaves some out.', required: false },
    times: { description: "A service's next open times: their heading, the time zone and the days; with the showTimes option.", required: false },
    timesHeading: { description: "The heading over a service's open times.", required: false },
    zone: { description: "The visitor's time zone, named in words.", required: false },
    days: { description: 'The days with open times under a service, up to four.', required: false },
    day: { description: 'One day and its open times.', required: false },
    dayName: { description: 'The day of the week.', required: false },
    dayDate: { description: 'The month and day.', required: false },
    time: { description: "One open time; opens the service's page with it picked.", required: false, role: 'time' },
    timesEmpty: { description: 'The line shown when a service has no open times coming up, or they could not load.', required: false },
    mine: {
      description: "With the mine option, the signed-in client's next booking of the service: its line (Your next: and the time) and its button. Its state is where the booking stands.",
      required: false,
      states: ['going', 'held', 'approved', 'requested', 'waitlisted'],
    },
    mineWhen: { description: "The time in the mark's line, in the visitor's time zone.", required: false, role: 'time' },
    mineAction: {
      description: "The mark's button: the booking's own step while it waits on the client (Complete booking, Register, Pay), else View booking.",
      required: false,
      states: ['going', 'held', 'approved', 'requested', 'waitlisted'],
    },
  },
  actions: {
    open: { description: "Opens a service's page.", args: [{ name: 'serviceId', kind: 'item', list: 'services', key: 'id' }] },
    seeAll: { description: 'Opens the services page.', args: [] },
    openTime: {
      description: "Opens a service's page with one of its open times picked; a start that is not one of its shown times does nothing.",
      args: [
        { name: 'serviceId', kind: 'item', list: 'services', key: 'id' },
        { name: 'start', kind: 'value', type: 'string', format: 'text', maxLength: 40 },
      ],
    },
    openBooking: {
      description: "With the mine option, opens the client's next booking of a service: the step it waits on, else its page. A service with no mark does nothing.",
      args: [{ name: 'serviceId', kind: 'item', list: 'services', key: 'id' }],
    },
  },
  events: {
    opened: 'A visitor opened a service.',
  },
});

/** One open time under a service, ready to draw. */
export interface ServiceOpenTime {
  /** The start, a UTC instant (ISO 8601). */
  readonly startsAt: string;
  /** The start in the visitor's zone: "11:15 AM". */
  readonly label: string;
  /** The service page with this time picked: `<service path>?start=<UTC ISO>`. */
  readonly href: string;
}

/** One day with open times under a service, in the visitor's zone. */
export interface ServiceOpenDay {
  /** The visitor's calendar day, YYYY-MM-DD. */
  readonly date: string;
  /** The day of the week: "Tue". */
  readonly name: string;
  /** The month and day: "Oct 20". */
  readonly dateLabel: string;
  /** The whole date in words: "Tuesday, October 20". */
  readonly longLabel: string;
  /** Up to four open times, earliest first. */
  readonly times: readonly ServiceOpenTime[];
}

/** One service's next open times. */
export interface ServiceTimes {
  /** loading until its read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** Up to four days with open times; empty when the service has none coming up. */
  readonly days: readonly ServiceOpenDay[];
}

/** What the services piece knows. */
export interface ServicesState {
  /** loading until the first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The services to draw, in the studio's order, already cut to the limit option. */
  readonly services: readonly SiteServiceSummaryDto[];
  /** How many services the studio lists, before the limit option cuts them. */
  readonly total: number;
  /** The limit option left some out: See all services shows. */
  readonly hasMore: boolean;
  /** The services page's address. */
  readonly allServicesPath: string;
  /** With the showTimes option, each shown service's next open times by its id; empty without it. */
  readonly times: Readonly<Record<string, ServiceTimes>>;
  /** The visitor's device time zone (IANA). */
  readonly timezone: string;
  /** That zone in words: "Eastern Time (EDT)". */
  readonly zoneName: string;
  /** With the mine option, the signed-in client's next booking of each service they have booked, by the service's id; empty for a guest. */
  readonly marks: Readonly<Record<string, MineMark>>;
}

/** What a drawing can ask the services piece to do. */
export interface ServicesActions {
  /** Open the page of the service with this id. */
  readonly open: (serviceId: string) => void;
  /** Open the services page. */
  readonly seeAll: () => void;
  /** Open the page of the service with this id with the open time starting at this UTC instant picked; any other start is ignored. */
  readonly openTime: (serviceId: string, start: string) => void;
  /** Open the client's next booking of the service with this id: the step it waits on, else its page. A service with no mark does nothing. */
  readonly openBooking: (serviceId: string) => void;
}

/** What the services piece tells its host. */
export interface ServicesEvents {
  /** A visitor opened a service; carries its id. */
  readonly opened: { readonly serviceId: string };
}
